<template>
  <Disclosure as="nav" class="bg-white dark:bg-gray-800 shadow sticky top-0 z-50" v-slot="{ close }">
    <div class="mx-auto w-full px-2 sm:px-4 lg:px-8">
      <div class="flex justify-between h-16">
        <div class="flex px-2 lg:px-0">
          <div class="flex-shrink-0 flex items-center">
            <RouterLink :to="logoRoute" class="flex items-center" aria-label="Home">
              <img class="logo block lg:hidden" :src="getLogoPath" alt="Your Company">
              <img class="logo hidden lg:block" :src="getLogoPath" alt="Your Company">
            </RouterLink>
          </div>
          <div class="hidden lg:ml-6 lg:flex lg:space-x-8">
            <template v-for="(item, index) in filteredMenuItems" :key="index">
              <!-- If item has submenu -> HeadlessUI Menu -->
              <Menu v-if="item.submenu && item.submenu.length" as="div" class="relative mt-5"
                :key="`main-${index}-${menuNonce}`">
                <MenuButton
                  class="inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium leading-5 focus:outline-none transition duration-150 ease-in-out dark:text-gray-100"
                  :class="item.current ? 'border-yellow-500 text-gray-900' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'">
                  {{ item.text }}
                  <span v-if="item.text === 'Tickets' && isInternalUser" class="text-red-600">({{ undelegatedCount }})</span>
                  <ChevronDownIcon class="ml-2 h-5 w-5" aria-hidden="true" />
                </MenuButton>

                <transition enter-active-class="transition ease-out duration-100"
                  enter-from-class="transform opacity-0 scale-95" enter-to-class="transform opacity-100 scale-100"
                  leave-active-class="transition ease-in duration-75" leave-from-class="transform opacity-100 scale-100"
                  leave-to-class="transform opacity-0 scale-95">
                  <MenuItems
                    class="origin-top-right absolute z-10 mt-2 w-56 rounded-md shadow-lg py-1 bg-white ring-1 ring-black ring-opacity-5 focus:outline-none dark:bg-gray-800">
                    <MenuItem v-for="submenuItem in item.submenu" :key="submenuItem" v-slot="{ active }"
                      :as="RouterLink" :to="routeFor(submenuItem)" class="block px-4 py-2 text-sm"
                      :class="active ? 'bg-gray-100 dark:bg-gray-700 text-gray-900' : 'text-gray-700 dark:text-gray-100'">
                      {{ submenuItem }}
                      <span v-if="submenuItem === 'Delegate' && isInternalUser" class="text-red-600">
                        ({{ undelegatedCount }})
                      </span>
                    </MenuItem>
                  </MenuItems>
                </transition>
              </Menu>

              <!-- No submenu -> plain RouterLink so right-click works -->
              <RouterLink v-else :to="routeFor(item.text)"
                class="inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium leading-5 transition duration-150 ease-in-out"
                :class="item.current ? 'border-yellow-500 text-gray-900 dark:text-gray-100' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-100'">
                {{ item.text }}
              </RouterLink>
            </template>
          </div>
        </div>

        <!-- Mobile hamburger -->
        <div class="flex lg:hidden">
          <DisclosureButton
            class="p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-yellow-500">
            <span class="sr-only">Open main menu</span>
            <Bars3Icon class="h-6 w-6" aria-hidden="true" />
          </DisclosureButton>
        </div>

        <!-- Right section -->
        <div class="hidden lg:flex lg:items-center">
          <div class="flex items-center justify-center">
            <button @click="toggleDarkMode"
              class="flex items-center bg-gray-400 dark:bg-gray-400 p-0.5 rounded-full w-14 h-6 focus:outline-none">
              <!-- Moon -->
              <svg v-show="isDark" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"
                stroke="currentColor" class="h-4 w-4 text-gray-500 dark:text-gray-100 ml-1">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M21 12.38A9 9 0 11 6.62 3 7 7 0 0021 12.38z" />
              </svg>
              <!-- Sun -->
              <svg v-show="!isDark" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"
                stroke="currentColor" class="h-4 w-4 text-yellow-500 ml-1">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M12 2v2m0 16v2m10-10h2M2 12h2m14.48 7.02l1.42 1.42M4.1 6.1l1.42-1.42m0 11.48l1.42 1.42M4.1 4.1l1.42 1.42m11.48 11.48l1.42 1.42M19.9 6.1l1.42-1.42M12 6a6 6 0 100 12 6 6 0 000-12z" />
              </svg>
              <span :class="isDark ? 'translate-x-3' : 'translate-x-0'"
                class="inline-block bg-white rounded-full h-4 w-4 transform transition-transform ml-1"></span>
            </button>
          </div>

          <!-- Profile dropdown -->
          <Menu as="div" class="ml-4 relative flex-shrink-0" :key="`profile-${menuNonce}`">
            <div>
              <MenuButton
                class="bg-gray-800 flex text-sm rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-800 focus:ring-white">
                <div
                  class="h-8 w-8 rounded-full flex items-center justify-center bg-gray-400 border-gray-400 dark:bg-gray-600 dark:border-gray-600 text-white">
                  <svg class="h-6 w-6 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"
                    stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                      d="M5.121 17.804A4 4 0 018 16h8a4 4 0 012.879 1.804M12 14a4 4 0 100-8 4 4 0 000 8z" />
                  </svg>
                </div>
              </MenuButton>
            </div>

            <transition enter-active-class="transition ease-out duration-100"
              enter-from-class="transform opacity-0 scale-95" enter-to-class="transform opacity-100 scale-100"
              leave-active-class="transition ease-in duration-75" leave-from-class="transform opacity-100 scale-100"
              leave-to-class="transform opacity-0 scale-95">
              <MenuItems
                class="origin-top-right absolute right-0 mt-2 w-48 rounded-md shadow-lg py-1 bg-white ring-1 ring-black ring-opacity-5 focus:outline-none">
                <!-- Profile as RouterLink (right-clickable) -->
                <MenuItem v-slot="{ active }" :as="RouterLink" :to="routeFor('Profile')" class="block px-4 py-2 text-sm"
                  :class="active ? 'bg-gray-100 text-gray-900' : 'text-gray-700'">
                  Your Profile
                </MenuItem>

                <!-- Help (internal staff only — the guide documents internal pages; extTech excluded) -->
                <MenuItem v-if="isInternalUser" v-slot="{ active }" as="button" @click="openDoc('docs/help')"
                  class="block w-full text-left px-4 py-2 text-sm"
                  :class="active ? 'bg-gray-100 text-gray-900' : 'text-gray-700'">
                  Help
                </MenuItem>

                <!-- What's New (internal staff only; extTech excluded; highlighted for 3 days after a new entry) -->
                <MenuItem v-if="isInternalUser" v-slot="{ active }" as="button" @click="openDoc('docs/whats-new')"
                  data-link="whats-new" class="block w-full text-left px-4 py-2 text-sm"
                  :class="[active ? 'bg-gray-100 text-gray-900' : 'text-gray-700', { 'whats-new-highlight': whatsNewHighlight }]">
                  What's New
                </MenuItem>

                <!-- Sign out (button) -->
                <MenuItem v-slot="{ active }" as="button" @click="signOut"
                  class="block w-full text-left px-4 py-2 text-sm"
                  :class="active ? 'bg-gray-100 text-gray-900' : 'text-gray-700'">
                  Sign out
                </MenuItem>
              </MenuItems>
            </transition>
          </Menu>
        </div>
      </div>
    </div>

    <!-- Mobile menu -->
    <DisclosurePanel class="lg:hidden bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700">
      <div class="px-3 pt-3 pb-3 space-y-1">
        <template v-for="(item, index) in filteredMenuItems" :key="`m-${index}-${menuNonce}`">
          <!-- Parent with submenu: a section label + indented children -->
          <template v-if="item.submenu && item.submenu.length">
            <div class="px-3 pt-3 pb-1 text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
              {{ item.text }}
              <span v-if="item.text === 'Tickets' && isInternalUser" class="text-red-500 ml-1">({{ undelegatedCount }})</span>
            </div>
            <RouterLink v-for="submenuItem in item.submenu" :key="submenuItem"
              :to="routeFor(submenuItem)"
              @click="close()"
              class="block pl-6 pr-3 py-2 rounded-md text-base font-medium text-gray-700 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700">
              {{ submenuItem }}
              <span v-if="submenuItem === 'Delegate' && isInternalUser" class="text-red-500 ml-1">({{ undelegatedCount }})</span>
            </RouterLink>
          </template>

          <!-- Parent without submenu: plain link -->
          <RouterLink v-else :to="routeFor(item.text)"
            @click="close()"
            class="block px-3 py-2 rounded-md text-base font-medium text-gray-700 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700">
            {{ item.text }}
          </RouterLink>
        </template>
      </div>

      <!-- User / theme section -->
      <div class="border-t border-gray-200 dark:border-gray-700 px-3 py-3 space-y-1">
        <button @click="toggleDarkMode(); close()"
          class="w-full text-left px-3 py-2 rounded-md text-base font-medium text-gray-700 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700">
          {{ isDark ? '☀ Switch to light mode' : '🌙 Switch to dark mode' }}
        </button>
        <RouterLink :to="routeFor('Profile')" @click="close()"
          class="block px-3 py-2 rounded-md text-base font-medium text-gray-700 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700">
          Your Profile
        </RouterLink>
        <button @click="close(); signOut()"
          class="w-full text-left px-3 py-2 rounded-md text-base font-medium text-red-600 dark:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/30">
          Sign out
        </button>
      </div>
    </DisclosurePanel>
  </Disclosure>
</template>

<script setup>
import { Disclosure, DisclosureButton, DisclosurePanel, Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/vue'
import { ChevronDownIcon, Bars3Icon } from '@heroicons/vue/20/solid'
import { useRouter, RouterLink } from 'vue-router'
import ThemeService from '@/services/ThemeService'
import { ref, onMounted, computed, onBeforeUnmount, watch } from 'vue'
import { useJwt } from '@vueuse/integrations/useJwt'
import axios from 'axios'
import { useStore } from 'vuex'

const isDark = ref(false)
const router = useRouter()
const token = localStorage.getItem('token')
let isIntUser = ref(false)
let isInternalUser = ref(false)
let userroles = ref([])
let userPermissions = ref([])
const undelegatedCount = ref(0)
let pollingInterval
const store = useStore()
const menuNonce = ref(0)
const whatsNewHighlight = ref(false)

// Opens an auth-protected doc page (Help / What's New) in a new tab.
// The page is no longer a public static file — it's served by the backend
// behind auth:api, so we fetch it with the bearer token (attached by the
// global axios interceptor) and hand the resulting HTML to a new tab via a
// blob URL. The window is opened synchronously inside the click handler so
// popup blockers don't intercept it after the await.
const openDoc = async (endpoint) => {
  const win = window.open('', '_blank')
  try {
    const response = await axios.get(endpoint, { responseType: 'blob' })
    const url = URL.createObjectURL(response.data)
    if (win) {
      win.location = url
    } else {
      // Popup was blocked — fall back to a same-gesture anchor click.
      const a = document.createElement('a')
      a.href = url
      a.target = '_blank'
      a.rel = 'noopener'
      a.click()
    }
    setTimeout(() => URL.revokeObjectURL(url), 60000)
  } catch (e) {
    if (win) win.close()
    // A 401 is handled by the global interceptor (logout + redirect); other
    // errors surface via showGlobalError. Nothing else to do here.
  }
}

// Highlights the "What's New" link if there's a recent entry.
// Reads the latest-entry-date meta tag from the auth-protected What's New page
// and applies the highlight if the newest entry is within 3 days. Driven by a
// reactive ref because the dropdown items are not in the DOM until the menu is
// opened.
const checkWhatsNew = async () => {
  // Internal staff only — external clients and external techs (extTech) don't get
  // the Help / What's New links, and the backend rejects them for these routes.
  if (!isInternalUser.value) return
  try {
    const response = await axios.get('docs/whats-new', { responseType: 'text' })
    const html = response.data
    const match = html.match(/<meta\s+name=["']latest-entry-date["']\s+content=["']([^"']+)["']/i)
    if (!match) return
    const entryDate = new Date(match[1])
    const daysSinceEntry = (new Date() - entryDate) / (1000 * 60 * 60 * 24)
    if (daysSinceEntry <= 3) whatsNewHighlight.value = true
  } catch (e) {
    // Silent fail — highlight is non-critical
  }
}

if (token) {
  try {
    const { payload } = useJwt(token);
    isIntUser.value = payload.value.scopes && (payload.value.scopes.includes('internal') || payload.value.scopes.includes('extTech'));
    isInternalUser.value = payload.value.scopes && payload.value.scopes.includes('internal');
  } catch (e) {
    console.error('Invalid token:', e);
  }
};

const getLogoPath = computed(() => {
  return `/assets/logos/${store.state.logo || localStorage.getItem('logo')}`;
});

// Clicking the logo is a role-aware "home": internal staff and external techs
// go to Jobs; customer users/admins go to their client tickets list.
const logoRoute = computed(() => {
  return isIntUser.value ? { name: 'time' } : { name: 'viewClientTickets' };
});

const fetchUnassignedTicketsCount = async () => {
  try {
    if (isInternalUser.value) {
      const response = await axios.get(`${axios.defaults.baseURL}ticket/new-count`);
      const newCount = response.data;
      if (newCount !== undelegatedCount.value) {
        undelegatedCount.value = newCount;
        if (router.currentRoute.value.name === 'delegator') {
          store.commit('triggerReloadDelegatorTickets');
        }
      }
    }
  } catch (error) {
    console.error('Error fetching unassigned tickets count:', error);
  }
};

const getUserRole = async () => {
  try {
    const roleResponse = await axios.get(`user/role/${localStorage.getItem('user_id')}`);
    userroles.value = roleResponse.data.roles;
    userPermissions.value = roleResponse.data.user_permissions || [];
  } catch (error) {
    console.error('Error fetching user role:', error);
  }
};

const startPolling = () => {
  pollingInterval = setInterval(fetchUnassignedTicketsCount, 30000);
};
const stopPolling = () => {
  if (pollingInterval) clearInterval(pollingInterval);
};

const routeFor = (label) => {
  const routes = {
    'Delegate': 'delegator',
    'Create Ticket': 'newTicket',
    'View Tickets': 'viewTickets',
    'View Schedules': 'ticketSchedules',
    'Create Schedule': 'newTicketSchedule',
    'New Support Request': 'newSupportTicket',
    'View Requests': 'viewClientTickets',
    'Profile': isIntUser.value ? 'profile' : 'extProfile',
    'Assets': 'assets',
    'Jobs': 'time',
    'Rate Sheet': 'feeCalc',
    'Reporting': 'reportingAll',
    'Users': 'userAdmin',
  }
  return routes[label] ? { name: routes[label] } : { name: 'dashboard' };
};
const hasUserPermission = (module, name) => {
  return (userPermissions.value || []).some(p =>
    p.module === module &&
    p.name === name &&
    String(p.value) !== '0'
  );
};
const canSeeTicketScheduler = computed(() => {
  const roles = userroles.value || [];
  // Ticket scheduler is internal-only — external roles (60/70/80/100) never see it
  // regardless of any explicit ticket_scheduler.access permission.
  const isExternal = roles.some(r => [60, 70, 80, 100].includes(Number(r)));
  if (isExternal) return false;
  const byRole = roles.includes(10) || roles.includes(20);
  return byRole || hasUserPermission('ticket_scheduler', 'access');
});
const canSeeAssetsExternal = computed(() => {
  const roles = userroles.value || [];
  const is60 = roles.includes(60);
  const is70 = roles.includes(70);
  return is60 || (is70 && hasUserPermission('assets', 'access'));
});
const canSeeAssetsInternal = computed(() => {
  const roles = userroles.value || [];
  // Internal admin roles (10/20/30) and extTech (80, which inherits the internal nav)
  // see Assets natively. Sales rep (90) is the one internal-scope role that does not —
  // they must be granted the assets.access override. Role 60 (Customer Admin) takes
  // the external path (canSeeAssetsExternal) and isn't relevant here.
  const nativeInternal = roles.some(r => [10, 20, 30, 80].includes(Number(r)));
  return nativeInternal || hasUserPermission('assets', 'access');
});
const canSeeUserAdmin = computed(() => {
  const roles = userroles.value || [];
  return roles.some(r => [10, 20, 30].includes(Number(r)));
});
const filteredMenuItems = computed(() => {
  const roles = userroles.value || [];
  const canSeeRateSheet = isInternalUser.value || roles.includes(90);
  const canSeeReporting = roles.some(r => Number(r) <= 30);
  const isExtTech = roles.includes(80);
  if (isIntUser.value) {
    return menuItems.value
      .filter(item => {
        if (item.text === 'Rate Sheet') return canSeeRateSheet;
        if (item.text === 'Ticket Scheduler') return canSeeTicketScheduler.value;
        if (item.text === 'Assets') return canSeeAssetsInternal.value;
        if (item.text === 'Reporting') return canSeeReporting;
        if (item.text === 'Admin') return canSeeUserAdmin.value;
        return true;
      })
      .map(item => {
        if (isExtTech && Array.isArray(item.submenu) && item.submenu.includes('Delegate')) {
          return { ...item, submenu: item.submenu.filter(s => s !== 'Delegate') };
        }
        return item;
      });
  }

  return extMenuItems.value.filter(item => {
    if (item.text === 'Assets') return canSeeAssetsExternal.value;
    return Array.isArray(item.roles) && item.roles.some(r => roles.includes(r));
  });
});
const menuItems = ref([
  { text: 'Jobs', current: false, submenu: [] },
  { text: 'Tickets', current: false, submenu: ['Delegate', 'View Tickets', 'Create Ticket'] },
  { text: 'Ticket Scheduler', current: false, submenu: ['View Schedules', 'Create Schedule'] },
  { text: 'Rate Sheet', current: false, submenu: [] },
  { text: 'Assets', current: false, submenu: [] },
  { text: 'Reporting', current: false, submenu: [] },
  { text: 'Admin', current: false, submenu: ['Users'] },
]);

const extMenuItems = ref([
  { text: 'Tickets', current: false, submenu: ['View Requests', 'New Support Request'], roles: [60, 70] },
  { text: 'Assets', current: false, submenu: [], roles: [60, 70] }
]);

const updateMenuCurrent = (route) => {
  const fullPath = route.fullPath;
  const name = route.name;
  const map = [
    { match: n => ['time', 'addEditJob', 'userJobsList', 'dashboard'].includes(n) || fullPath.startsWith('/job'), menu: 'Jobs' },
    { match: n => ['delegator', 'viewTickets', 'newTicket', 'viewClientTickets', 'newSupportTicket'].includes(n) || fullPath.startsWith('/ticket/'), menu: 'Tickets' },
    { match: n => ['ticketSchedules', 'newTicketSchedule', 'editTicketSchedule'].includes(n) || fullPath.startsWith('/ticket-schedules'), menu: 'Ticket Scheduler' },
    { match: n => n == 'feeCalc' || fullPath.startsWith('/feeCalc'), menu: 'Rate Sheet' },
    { match: n => n == 'assets' || fullPath.startsWith('/network-assets'), menu: 'Assets' },
    { match: n => fullPath.startsWith('/reporting'), menu: 'Reporting' },
    { match: n => ['userAdmin', 'userAdminEdit'].includes(n) || fullPath.startsWith('/admin'), menu: 'Admin' },
  ];
  const activeMain = map.find(entry => entry.match(name))?.menu;
  if (isIntUser.value) {
    menuItems.value = menuItems.value.map(item => ({ ...item, current: item.text === activeMain }));
  } else {
    extMenuItems.value = extMenuItems.value.map(item => ({ ...item, current: item.text === activeMain }));
  }
};

watch(
  () => router.currentRoute.value,
  (to) => {
    updateMenuCurrent(to)
    menuNonce.value++
  },
  { immediate: true }
);
const signOut = () => {
  localStorage.removeItem('token');
  localStorage.removeItem('user_id');
  localStorage.removeItem('internal');
  window.location.href = `${window.location.origin}/login`;
};

const toggleDarkMode = () => {
  ThemeService.toggleDarkMode();
  isDark.value = !isDark.value;
};

onMounted(() => {
  ThemeService.loadThemePreference();
  isDark.value = ThemeService.getCurrentMode();
  getUserRole();
  fetchUnassignedTicketsCount();
  startPolling();
  checkWhatsNew();
});

onBeforeUnmount(() => stopPolling());
</script>

<style scoped>
.logo {
  width: 160px;
}

button svg {
  transition: color 0.2s ease-in-out;
}

/* "New" badge on the What's New link when there's a recent entry. */
.whats-new-highlight::after {
  content: "New";
  display: inline-block;
  margin-left: 0.4rem;
  padding: 0 0.4rem;
  font-size: 0.65rem;
  font-weight: 600;
  line-height: 1.1rem;
  color: #92400e;
  background-color: #fef3c7;
  border-radius: 9999px;
  vertical-align: middle;
}
</style>
